import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

import { type Variants } from 'framer-motion';

import { Portal, PortalArea, YatSprite } from '@pkg/components';

import { ToastContent, ToastIcon, ToastPill, ToastText, ToastWrapper } from './styles';

const DELAY_TIMER = 0;
const HIDE_TIMER = 10000;

const ToastWrapperTransition: Variants = {
    showing: {
        y: 0,
        opacity: 1,
        scale: 1,
        transition: {
            ease: 'anticipate',
            duration: 1,
        },
    },
    hidden: {
        y: '100%',
        opacity: 0,
        scale: 0.5,
        transition: {
            ease: 'anticipate',
            duration: 0.5,
        },
    },
};

export interface ToastProps {
    emoji?: string;
    message: string;
    timeout?: number;
    delay?: number;
}

export const Toast = ({ emoji, message, timeout = HIDE_TIMER, delay = DELAY_TIMER }: ToastProps) => {
    const hideTimeout = useRef<ReturnType<typeof setTimeout>>();

    const [show, setShow] = useState(false);
    const [rendered, setRendered] = useState(true);

    const handleShow = () => setShow(true);
    const handleHide = () => setShow(false);

    const clearTimer = useCallback((timerRef) => {
        timerRef.current && clearTimeout(timerRef.current);
    }, []);

    useLayoutEffect(() => {
        handleShow();
    }, []);

    useEffect(() => {
        hideTimeout.current = setTimeout(handleHide, timeout + delay);

        return () => {
            clearTimer(hideTimeout);
        };
    }, [timeout, delay, hideTimeout, clearTimer]);

    const handleAnimation = (definition: string) => {
        if (definition === 'showing') {
            setRendered(true);
            handleShow();
        }
        if (definition === 'hidden') {
            setRendered(false);
            handleHide();
        }
    };

    if (!rendered) return null;

    return (
        <Portal destination={PortalArea.Transition}>
            <ToastWrapper
                variants={ToastWrapperTransition}
                initial="hidden"
                animate={show ? 'showing' : 'hidden'}
                onAnimationStart={handleAnimation}
                onAnimationComplete={handleAnimation}
            >
                <ToastPill onClick={handleHide}>
                    <ToastContent $hasEmoji={Boolean(emoji)}>
                        {emoji && (
                            <ToastIcon>
                                <YatSprite emojiID={emoji} />
                            </ToastIcon>
                        )}
                        <ToastText>{message}</ToastText>
                    </ToastContent>
                </ToastPill>
            </ToastWrapper>
        </Portal>
    );
};
